/**
 * One assigned thing, placed where the admin put it.
 *
 * Every other case in `DropSectionRenderer` renders a KIND — whatever
 * leaderboards or quests are attached to the Drop. This one renders the
 * specific thing the section names, which is what lets a publisher order their
 * page.
 *
 * Returns null when the assignment no longer resolves. `engagementName` is
 * filled server-side from the live record, so its absence means the target was
 * deleted or is unreachable — and a heading over an empty space is the exact
 * failure this Drop surface has produced before.
 *
 * Its own module because the dispatcher it came from outgrew the 400-line
 * ceiling; it is the second-longest case there.
 */
import React from "react";
import {ChallengesSection} from "@/widgets/ChallengesSection";
import {LedgerBoard} from "@/widgets/LedgerBoard";
import {GamificationRenderer} from "@/components/gamification/GamificationRenderer";
import {MissionsCase} from "./sectionCases";
import type {DropSection} from "@/hooks/drop/useGetDrop";
import type {DropRenderContext} from "./dropRenderContext";

export function renderEngagementSection(section: DropSection, ctx: DropRenderContext): React.ReactNode {
    const {drop, slug, competitionId} = ctx;
    /* engagementName is a TITLE fallback, not a render requirement — requiring it silently dropped named-by-label sections. */
    if (!section.engagementId) return null;

    switch (section.engagementType) {
        case "COMPETITION":
            /*
             * ChallengesSection is the competition surface — it takes a
             * competitionId and renders whatever that competition is
             * (leaderboard, ladder, league). The assigned id overrides the
             * Drop's umbrella, which is the entire point of assigning it.
             */
            return <ChallengesSection
                competitionId={section.engagementId}
                heroVideoUrl={drop?.heroSection?.videoUrl}
                heroImageUrl={drop?.heroSection?.heroImageUrl || drop?.creatorHeaderImageUrl}
                name={drop?.name}
                hostId={drop?.id}
            />;
        case "CHALLENGE":
        case "QUEST":
            /*
             * Both resolve through the missions surface: a QuestConfig is what
             * a Challenge holds, and MissionsCase already takes a challenge id
             * and renders its active configs.
             */
            return <MissionsCase
                challengeId={section.engagementId}
                hubBrandName={drop?.name}
                hubBrandColor={drop?.themeColors?.primary as string | undefined}
            />;
        case "GAMIFICATION":
            /*
             * Same mount as a Hub content block
             * (`ContentBlockRenderer.tsx:715-721`) — all 26 types, LOCK_IN
             * included. Left unwired until now on the reasoning that a
             * gamification "cannot be HOSTED by a Drop", which was the
             * wrong diagnosis: the blocker was eligibility having no way to
             * name a Drop audience, fixed by `HOST_MEMBERS` (spine 3.0).
             */
            return section.engagementId
                ? <GamificationRenderer
                    gamificationModelId={section.engagementId}
                    displayMode="full"
                    sectionTitle={section.label}
                />
                : null;
        case "LEDGER_BOARD":
            /*
             * A LedgerBoard board (attach model T2). The section names the
             * board; the board names its ledger; earning routes there via
             * the host walk. Same widget the content-block path mounts —
             * one LedgerBoard, any surface.
             */
            return <LedgerBoard
                config={{ledgerBoardId: section.engagementId}}
                sectionTitle={section.label || section.engagementName}
            />;
        case "ACTIVATION":
            /*
             * ACTIVATION has no widget of its own — `ActivationBlock` is a
             * placeholder — so it falls through to the competition surface
             * when the assignment carries one, and is skipped otherwise.
             * GAMIFICATION used to share this branch; see the case above.
             */
            return competitionId
                ? <ChallengesSection
                    competitionId={competitionId}
                    heroVideoUrl={drop?.heroSection?.videoUrl}
                    heroImageUrl={drop?.heroSection?.heroImageUrl || drop?.creatorHeaderImageUrl}
                    name={drop?.name}
                    hostId={drop?.id}
                />
                : null;
        default:
            return null;
    }
}
